import { isLocalVideoLabPage } from '@/utils/localVideoLab'
import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useAdminAttention } from '@/composables/useAdminAttention'

function lazyLoad(importFn: () => Promise<any>) {
  return () => importFn().catch((err: any) => {
    const msg = err?.message || ''
    if (
      msg.includes('Failed to fetch dynamically imported module') ||
      msg.includes('Importing a module script failed') ||
      msg.includes('error loading dynamically imported module') ||
      msg.includes('text/html')
    ) {
      const lastReload = sessionStorage.getItem('chunk_reload')
      const now = Date.now()
      if (!lastReload || now - Number(lastReload) > 10000) {
        sessionStorage.setItem('chunk_reload', String(now))
        window.location.reload()
        return
      }
    }
    throw err
  })
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: lazyLoad(() => import('@/views/LoginView.vue')),
      meta: { requiresAuth: false }
    },
    {
      path: '/2fa/verify',
      name: '2fa-verify',
      component: lazyLoad(() => import('@/views/TwoFactorVerifyView.vue')),
      meta: { requiresAuth: false }
    },
    {
      path: '/2fa/setup',
      name: '2fa-setup',
      component: lazyLoad(() => import('@/views/TwoFactorSetupView.vue')),
      meta: { requiresAuth: true }
    },
    {
      path: '/profile',
      name: 'profile',
      component: lazyLoad(() => import('@/views/ProfileView.vue')),
      meta: { requiresAuth: true, title: 'Профиль' }
    },
    {
      path: '/',
      name: 'dashboard',
      component: lazyLoad(() => import('@/views/DashboardView.vue')),
      meta: { requiresAuth: true }
    },
    {
      path: '/analytics',
      name: 'analytics',
      component: lazyLoad(() => import('@/views/AnalyticsView.vue')),
      meta: { requiresAuth: true, title: 'Аналитика' }
    },
    {
      path: '/championships',
      name: 'championships',
      component: lazyLoad(() => import('@/views/ChampionshipsListView.vue')),
      meta: { requiresAuth: true, title: 'Чемпионаты' }
    },
    {
      path: '/championships/edition/:editionId/monitor',
      name: 'championship-edition-monitor',
      component: lazyLoad(() => import('@/views/EditionMonitorView.vue')),
      meta: { requiresAuth: true, title: 'Монитор издания' }
    },
    {
      path: '/championships/:championshipId',
      name: 'championship-manage',
      component: lazyLoad(() => import('@/views/ChampionshipManageView.vue')),
      meta: { requiresAuth: true, title: 'Управление чемпионатом' }
    },
    {
      path: '/championships/:championshipId/editions/:editionId',
      name: 'championship-edition-manage',
      component: lazyLoad(() => import('@/views/EditionManageView.vue')),
      meta: { requiresAuth: true, title: 'Управление изданием' }
    },
    {
      path: '/championships/:championshipId/builder',
      name: 'championship-format-builder',
      component: lazyLoad(() => import('@/views/FormatBuilderView.vue')),
      meta: { requiresAuth: true, title: 'Конструктор формата' }
    },
    {
      path: '/bonuses',
      name: 'bonuses',
      component: lazyLoad(() => import('@/views/BonusesView.vue')),
      meta: { requiresAuth: true }
    },
    {
      path: '/bonuses/grant',
      name: 'bonuses-grant',
      component: lazyLoad(() => import('@/views/BonusGrantView.vue')),
      meta: { requiresAuth: true }
    },
    {
      path: '/bonuses/list',
      name: 'bonuses-list',
      component: lazyLoad(() => import('@/views/BonusListView.vue')),
      meta: { requiresAuth: true }
    },
    {
      path: '/bonuses/rules',
      name: 'bonus-rules',
      component: lazyLoad(() => import('@/views/BonusRulesView.vue')),
      meta: { requiresAuth: true }
    },
    {
      path: '/achievements',
      name: 'achievements',
      component: lazyLoad(() => import('@/views/AchievementsView.vue')),
      meta: { requiresAuth: true, title: 'Достижения' }
    },
    {
      path: '/club-levels',
      name: 'club-levels',
      component: lazyLoad(() => import('@/views/ClubLevelsView.vue')),
      meta: { requiresAuth: true, title: 'Уровни клубов' }
    },
    {
      path: '/referrals',
      name: 'referrals',
      component: lazyLoad(() => import('@/views/ReferralsView.vue')),
      meta: { requiresAuth: true }
    },
    {
      path: '/users',
      name: 'users',
      component: lazyLoad(() => import('@/views/UsersView.vue')),
      meta: { requiresAuth: true }
    },
    {
      path: '/statistics',
      name: 'statistics',
      component: lazyLoad(() => import('@/views/StatisticsView.vue')),
      meta: { requiresAuth: true }
    },
    {
      path: '/reports',
      name: 'reports',
      component: lazyLoad(() => import('@/views/ReportsView.vue')),
      meta: { requiresAuth: true, title: 'Жалобы на контент' }
    },
    {
      path: '/banned-users',
      name: 'banned-users',
      component: lazyLoad(() => import('@/views/BannedUsersView.vue')),
      meta: { requiresAuth: true, title: 'Заблокированные пользователи' }
    },
    {
      path: '/users/:id',
      name: 'user-detail',
      component: lazyLoad(() => import('@/views/UserDetailView.vue')),
      meta: { requiresAuth: true, title: 'Детали пользователя' }
    },
    {
      path: '/matches',
      name: 'matches',
      component: lazyLoad(() => import('@/views/MatchesView.vue')),
      meta: { requiresAuth: true, title: 'Матчи' }
    },
    {
      path: '/matches/:id',
      name: 'match-detail',
      component: lazyLoad(() => import('@/views/MatchDetailView.vue')),
      meta: { requiresAuth: true, title: 'Детали матча' }
    },
    {
      // Разбор аналитики CV и правки матча: смотреть и править могут только admin/super_admin
      path: '/matches/:id/review',
      name: 'match-review',
      component: lazyLoad(() => import('@/views/MatchReviewView.vue')),
      meta: { requiresAuth: true, requiresAdmin: true, title: 'Разбор аналитики' }
    },
    {
      // Границы таймов по видео (мобильный инструмент): начало и конец каждого тайма
      path: '/matches/:id/periods',
      name: 'match-periods',
      component: lazyLoad(() => import('@/views/MatchPeriodsView.vue')),
      meta: { requiresAuth: true, requiresAdmin: true, title: 'Границы таймов' }
    },
    {
      path: '/stadiums',
      name: 'stadiums',
      component: lazyLoad(() => import('@/views/StadiumsView.vue')),
      meta: { requiresAuth: true, title: 'Стадионы' }
    },
    {
      path: '/stadiums/:id',
      name: 'stadium-detail',
      component: lazyLoad(() => import('@/views/StadiumDetailView.vue')),
      meta: { requiresAuth: true, title: 'Детали стадиона' }
    },
    {
      // Единый инбокс поддержки: стадионы + тренеры в одном списке с метками
      path: '/support',
      name: 'support',
      component: lazyLoad(() => import('@/views/SupportConversationsView.vue')),
      meta: { requiresAuth: true, title: 'Поддержка' }
    },
    {
      path: '/wallets',
      name: 'wallets',
      component: lazyLoad(() => import('@/views/WalletsView.vue')),
      meta: { requiresAuth: true, title: 'Кошельки' }
    },
    {
      path: '/finance',
      name: 'finance',
      component: lazyLoad(() => import('@/views/FinanceView.vue')),
      meta: { requiresAuth: true, title: 'Финансы' }
    },
    {
      path: '/finance-custody',
      name: 'finance-custody',
      component: lazyLoad(() => import('@/views/CustodyView.vue')),
      meta: { requiresAuth: true, title: 'Движение денег' }
    },
    {
      path: '/devices',
      name: 'devices',
      component: lazyLoad(() => import('@/views/DevicesView.vue')),
      meta: { requiresAuth: true, title: 'Устройства iGo' }
    },
    {
      path: '/devices/:id',
      name: 'device-detail',
      component: lazyLoad(() => import('@/views/DeviceDetailView.vue')),
      meta: { requiresAuth: true, title: 'Устройство' }
    },
    {
      path: '/sensor-tags',
      name: 'sensor-tags',
      component: lazyLoad(() => import('@/views/SensorTagsView.vue')),
      meta: { requiresAuth: true, requiresAdmin: true, title: 'Метки (датчики)' }
    },
    {
      path: '/logs',
      name: 'logs',
      component: lazyLoad(() => import('@/views/LogsView.vue')),
      meta: { requiresAuth: true, title: 'Журналы' }
    },
    {
      path: '/device-reports',
      name: 'device-reports',
      component: lazyLoad(() => import('@/views/DeviceReportsView.vue')),
      meta: { requiresAuth: true, title: 'Репорты по оборудованию' }
    },
    {
      path: '/apps',
      name: 'apps',
      component: lazyLoad(() => import('@/views/ApplicationsListView.vue')),
      meta: { requiresAuth: true, title: 'Приложения' }
    },
    {
      path: '/apps/releases',
      name: 'apps-releases',
      component: lazyLoad(() => import('@/views/ReleasesView.vue')),
      meta: { requiresAuth: true, title: 'Релизы iGo Live' }
    },
    {
      path: '/apps/api-keys',
      name: 'api-keys',
      component: lazyLoad(() => import('@/views/ApiKeysView.vue')),
      meta: { requiresAuth: true, title: 'API ключи' }
    },
    {
      path: '/referees',
      name: 'referees',
      component: lazyLoad(() => import('@/views/RefereesView.vue')),
      meta: { requiresAuth: true, title: 'Рефери' }
    },
    {
      path: '/referees/create',
      name: 'referee-create',
      component: lazyLoad(() => import('@/views/RefereeCreateView.vue')),
      meta: { requiresAuth: true, title: 'Создание рефери' }
    },
    {
      path: '/referees/rates',
      name: 'referee-rates',
      component: lazyLoad(() => import('@/views/RefereeRatesView.vue')),
      meta: { requiresAuth: true, title: 'Ставки рефери' }
    },
    {
      path: '/referees/applications',
      name: 'referee-applications',
      component: lazyLoad(() => import('@/views/RefereeApplicationsView.vue')),
      meta: { requiresAuth: true, title: 'Заявки рефери' }
    },
    {
      path: '/training-clubs',
      name: 'training-clubs',
      component: lazyLoad(() => import('@/views/TrainingClubsView.vue')),
      meta: { requiresAuth: true, title: 'Тренерские клубы' }
    },
    {
      path: '/club-closure',
      name: 'club-closure',
      component: lazyLoad(() => import('@/views/ClubClosureView.vue')),
      // Сводка содержит баланс и банковские снимки — только admin/super_admin (контракт §5.1).
      meta: { requiresAuth: true, requiresAdmin: true, title: 'Разбор закрытого клуба' }
    },
    {
      path: '/club-closure/:id',
      name: 'club-closure-detail',
      component: lazyLoad(() => import('@/views/ClubClosureView.vue')),
      meta: { requiresAuth: true, requiresAdmin: true, title: 'Разбор закрытого клуба' }
    },
    {
      path: '/referees/:id',
      name: 'referee-detail',
      component: lazyLoad(() => import('@/views/RefereeDetailView.vue')),
      meta: { requiresAuth: true, title: 'Детали рефери' }
    },
    {
      path: '/coaches',
      name: 'coaches',
      component: lazyLoad(() => import('@/views/CoachesView.vue')),
      meta: { requiresAuth: true, title: 'Тренеры' }
    },
    {
      path: '/coaches/applications',
      name: 'coach-applications',
      component: lazyLoad(() => import('@/views/CoachApplicationsView.vue')),
      meta: { requiresAuth: true, title: 'Заявки тренеров' }
    },
    {
      // Легаси-путь: поддержка тренеров теперь в едином инбоксе /support (фильтр «Тренеры»)
      path: '/coaches/support',
      redirect: '/support'
    },
    {
      path: '/ai-pipeline',
      name: 'ai-pipeline',
      component: lazyLoad(() => import('@/views/AiPipelineView.vue')),
      meta: { requiresAuth: true, title: 'AI Pipeline' }
    },
    {
      path: '/active-pipelines',
      name: 'active-pipelines',
      component: lazyLoad(() => import('@/views/ActivePipelinesView.vue')),
      meta: { requiresAuth: true, title: 'Активные пайплайны' }
    },
    {
      path: '/field-sizes',
      name: 'field-sizes',
      component: lazyLoad(() => import('@/views/FieldSizesView.vue')),
      meta: { requiresAuth: true, title: 'Размеры полей' }
    },
    {
      path: '/ai-models',
      name: 'ai-models',
      component: lazyLoad(() => import('@/views/AiModelsView.vue')),
      meta: { requiresAuth: true, title: 'AI Модели' }
    },
    {
      path: '/advertisements',
      name: 'advertisements',
      component: lazyLoad(() => import('@/views/AdvertisementsView.vue')),
      meta: { requiresAuth: true, title: 'Реклама' }
    },
    {
      path: '/tariffs',
      name: 'tariffs',
      component: lazyLoad(() => import('@/views/TariffsView.vue')),
      meta: { requiresAuth: true, title: 'Тарифы' }
    },
    {
      path: '/tariff-finance',
      name: 'tariff-finance',
      component: lazyLoad(() => import('@/views/TariffFinanceView.vue')),
      meta: { requiresAuth: true, title: 'Финансы по тарифам' }
    },
    {
      path: '/payouts',
      name: 'payouts',
      component: lazyLoad(() => import('@/views/PayoutsView.vue')),
      meta: { requiresAuth: true, title: 'Выплаты' }
    },
    {
      path: '/finance-anomalies',
      name: 'finance-anomalies',
      component: lazyLoad(() => import('@/views/AnomaliesView.vue')),
      meta: { requiresAuth: true, title: 'Контроль утечек' }
    },
    {
      path: '/finance-ledger',
      name: 'finance-ledger',
      component: lazyLoad(() => import('@/views/LedgerView.vue')),
      meta: { requiresAuth: true, title: 'Реестр движений' }
    },
    {
      path: '/finance-queues',
      name: 'finance-queues',
      component: lazyLoad(() => import('@/views/FinancialQueuesView.vue')),
      alias: '/finance/queues',
      meta: { requiresAuth: true, title: 'Финансовые очереди' }
    },
    {
      path: '/bot',
      name: 'bot-settings',
      component: lazyLoad(() => import('@/views/BotSettingsView.vue')),
      meta: { requiresAuth: true, requiresAdmin: true, title: 'iGo Bot' }
    },
    {
      path: '/assistant-journal',
      name: 'assistant-journal',
      component: lazyLoad(() => import('@/views/AssistantJournalView.vue')),
      meta: { requiresAuth: true, requiresAdmin: true, title: 'Журнал помощника' }
    },
    {
      path: '/kit-orders',
      name: 'kit-orders',
      component: lazyLoad(() => import('@/views/KitOrdersView.vue')),
      meta: { requiresAuth: true, title: 'Заказы форм' }
    },
    {
      path: '/kit-presets',
      name: 'kit-presets',
      component: lazyLoad(() => import('@/views/KitPresetsView.vue')),
      meta: { requiresAuth: true, title: 'Каталог форм' }
    },
    {
      path: '/club-ratings',
      name: 'club-ratings',
      component: lazyLoad(() => import('@/views/ClubRatingsView.vue')),
      meta: { requiresAuth: true, title: 'Рейтинг клубов' }
    },
    {
      path: '/player-ratings',
      name: 'player-ratings',
      component: lazyLoad(() => import('@/views/PlayerRatingsView.vue')),
      meta: { requiresAuth: true, title: 'Рейтинг игроков' }
    }
  ]
})

router.afterEach((to) => {
  if (isLocalVideoLabPage(import.meta.env.VITE_VIDEO_LAB || import.meta.env.VITE_SENSOR_LAB, window.location.hostname, to.name)) return
  if (!to.meta.requiresAuth || !useAuthStore().isAuthenticated) return
  const { items, refreshAttention, markAttentionSeen } = useAdminAttention()
  void refreshAttention().then(() => {
    const matchingItems = items.value.filter(item => {
      const destination = router.resolve(item.route)
      if (destination.name !== to.name) return false
      return Object.entries(destination.query).every(([key, value]) => {
        const current = to.query[key]
        return Array.isArray(value)
          ? JSON.stringify(current) === JSON.stringify(value)
          : current === value
      })
    })
    for (const item of matchingItems) void markAttentionSeen(item.key)
  })
})

router.beforeEach(async (to, _from, next) => {
  // This worker-backed page is local diagnostics, with no account or match APIs.
  if (isLocalVideoLabPage(import.meta.env.VITE_VIDEO_LAB || import.meta.env.VITE_SENSOR_LAB, window.location.hostname, to.name)) {
    next(); return
  }
  const authStore = useAuthStore()

  // Восстанавливаем профиль и серверный статус 2FA до любых redirect-проверок.
  // Иначе первый deep-link с сохранённым токеном успевал пройти при пустом store.
  if (authStore.isAuthenticated && !authStore.isInitialized) {
    await authStore.initialize()
  }

  if (authStore.requires2FA && to.name !== '2fa-verify' && to.name !== 'login') {
    next({ name: '2fa-verify' })
    return
  }

  if (authStore.isAuthenticated && authStore.requires2FASetup && to.name !== '2fa-setup' && to.name !== 'login' && to.name !== 'profile') {
    next({ name: '2fa-setup' })
    return
  }

  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'login' })
  } else if (to.meta.requiresAdmin) {
    if (!authStore.isAuthenticated) next({ name: 'login' })
    else if (!authStore.isAdmin) next({ name: 'dashboard' })
    else next()
  } else if (to.name === 'login' && authStore.isAuthenticated) {
    next({ name: 'dashboard' })
  } else {
    next()
  }
})

export default router
